import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingCart, ArrowLeft, LogIn, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { CountdownTimer } from "@/components/CountdownTimer";
import { EmptyState } from "@/components/EmptyState";
import { EventImage } from "@/components/EventImage";
import { AuthModal } from "@/components/AuthModal";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { getEventPublicPath } from "@/lib/urls";
import { useState, useEffect } from "react";
import { validateCoupon } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatBRL } from "@/lib/utils";
import { useTranslation } from "react-i18next";

export default function Carrinho() {
  const { t } = useTranslation("checkout");
  const { items, removeItem, updateQuantity, clearCart, renewExpiry, subtotal, platformFee, expiresAt, couponCode, setCouponCode, discount, setDiscount, setAppliedCouponId, finalTotal } = useCart();
  const { user } = useAuth();
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [unavailableItems, setUnavailableItems] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (items.length === 0) return;
    const tierIds = items.map(i => i.tierId).filter(id => !id.startsWith("product-"));
    if (tierIds.length === 0) return;

    supabase
      .from("ticket_tiers")
      .select("id, quantity_total, quantity_sold, quantity_reserved, sale_start_date, sale_end_date")
      .in("id", tierIds)
      .then(({ data }) => {
        if (!data) return;
        const unavailable: string[] = [];
        const now = new Date();
        for (const item of items) {
          if (item.tierId.startsWith("product-")) continue;
          const tier = data.find(t => t.id === item.tierId);
          if (!tier) { unavailable.push(item.tierId); continue; }
          const available = tier.quantity_total - (tier.quantity_sold ?? 0) - (tier.quantity_reserved ?? 0);
          if (available < item.quantity) unavailable.push(item.tierId);
          if (tier.sale_start_date && new Date(tier.sale_start_date) > now) unavailable.push(item.tierId);
          if (tier.sale_end_date && new Date(tier.sale_end_date) < now) unavailable.push(item.tierId);
        }
        setUnavailableItems(unavailable);
        if (unavailable.length > 0) {
          toast({ title: t("cart.availabilityChangedTitle"), description: t("cart.availabilityChangedDesc"), variant: "destructive" });
        }
      });
  }, [items.length, t]);

  // Re-check availability every 30 seconds
  useEffect(() => {
    if (items.length === 0) return;
    const interval = setInterval(() => {
      const tierIds = items.map(i => i.tierId).filter(id => !id.startsWith("product-"));
      if (tierIds.length === 0) return;
      supabase
        .from("ticket_tiers")
        .select("id, quantity_total, quantity_sold, quantity_reserved, sale_start_date, sale_end_date")
        .in("id", tierIds)
        .then(({ data }) => {
          if (!data) return;
          const unavailable: string[] = [];
          const now = new Date();
          for (const item of items) {
            if (item.tierId.startsWith("product-")) continue;
            const tier = data.find(t => t.id === item.tierId);
            if (!tier) { unavailable.push(item.tierId); continue; }
            const available = tier.quantity_total - (tier.quantity_sold ?? 0) - (tier.quantity_reserved ?? 0);
            if (available < item.quantity) unavailable.push(item.tierId);
            if (tier.sale_start_date && new Date(tier.sale_start_date) > now) unavailable.push(item.tierId);
            if (tier.sale_end_date && new Date(tier.sale_end_date) < now) unavailable.push(item.tierId);
          }
          setUnavailableItems(unavailable);
        });
    }, 30000);
    return () => clearInterval(interval);
  }, [items]);

  // A11: Block checkout when items are unavailable
  const handleCheckout = () => {
    if (!user) { setShowAuth(true); return; }
    if (unavailableItems.length > 0) {
      toast({ title: t("cart.itemsUnavailableTitle"), description: t("cart.itemsUnavailableDesc"), variant: "destructive" });
      return;
    }
    navigate("/checkout");
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim() || items.length === 0) return;
    setValidatingCoupon(true);
    try {
      const eventId = items[0].eventId;
      const coupon = await validateCoupon(eventId, couponCode);
      if (coupon) {
        if (coupon.min_order_value && subtotal < coupon.min_order_value) {
          toast({ title: t("cart.couponMinOrderTitle"), description: t("cart.couponMinOrderDesc", { min: formatBRL(Number(coupon.min_order_value)) }), variant: "destructive" });
          setValidatingCoupon(false);
          return;
        }
        if (coupon.applicable_tier_ids && coupon.applicable_tier_ids.length > 0) {
          const cartTierIds = items.map(i => i.tierId);
          const hasValidTier = cartTierIds.some(tid => coupon.applicable_tier_ids!.includes(tid));
          if (!hasValidTier) {
            toast({ title: t("cart.couponNotApplicableTitle"), description: t("cart.couponNotApplicableDesc"), variant: "destructive" });
            setValidatingCoupon(false);
            return;
          }
        }

        // M05: Calculate discount only on applicable tiers
        let applicableSubtotal = subtotal;
        if (coupon.applicable_tier_ids && coupon.applicable_tier_ids.length > 0) {
          applicableSubtotal = items
            .filter(i => coupon.applicable_tier_ids!.includes(i.tierId))
            .reduce((sum, i) => sum + i.price * i.quantity, 0);
        }

        const discountAmount =
          coupon.discount_type === "percentage"
            ? applicableSubtotal * (coupon.discount_value / 100)
            : coupon.discount_value;
        setDiscount(Math.min(discountAmount, applicableSubtotal));
        setAppliedCouponId(coupon.id);
        toast({ title: t("cart.couponAppliedTitle"), description: t("cart.couponAppliedDesc", { amount: formatBRL(Math.min(discountAmount, applicableSubtotal)) }) });
      }
    } catch (err: any) {
      toast({ title: t("cart.couponInvalidTitle"), description: err?.message || t("cart.couponInvalidDesc"), variant: "destructive" });
      setDiscount(0);
      setAppliedCouponId(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="container pt-4 lg:pt-24 pb-16">
        <EmptyState
          icon={<ShoppingCart className="h-12 w-12" />}
          title={t("cart.emptyTitle")}
          description={t("cart.emptyDescription")}
          actionLabel={t("cart.viewEvents")}
          onAction={() => navigate("/eventos")}
        />
      </div>
    );
  }

  return (
    <div className="container pt-4 lg:pt-24 pb-16 pb-safe">
      <Link to="/eventos" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" /> {t("cart.continueShopping")}
      </Link>

      <div className="mb-8 space-y-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">{t("cart.title")}</h1>
          <p className="text-muted-foreground">{t("cart.subtitle")}</p>
        </div>
        {expiresAt && (
          <div className="flex items-center justify-between p-4 rounded-lg bg-warning/5 border border-warning/30">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-sm font-medium text-warning">
                <CountdownTimer expiresAt={expiresAt} onExpire={clearCart} hideIcon />
              </div>
              <span className="text-sm text-muted-foreground">{t("cart.reservationExpiresIn")}</span>
            </div>
            <Button variant="outline" size="sm" onClick={renewExpiry}>
              {t("cart.renew")}
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        <div className="flex-1 space-y-3 min-w-0">
          {items.map((item) => (
            <div key={item.tierId} className={`flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-xl border bg-card transition-all ${unavailableItems.includes(item.tierId) ? "border-destructive/50 bg-destructive/2" : "border-border/80 hover:border-primary/50 hover:shadow-sm"}`}>
              <div className="flex items-start gap-4 flex-1">
                {unavailableItems.includes(item.tierId) ? (
                  <AlertTriangle className="h-6 w-6 text-destructive shrink-0" />
                ) : (
                  <EventImage
                    src={item.coverImageUrl}
                    alt={item.eventTitle}
                    className="w-16 h-16 rounded-lg object-cover shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <Link to={getEventPublicPath(item.eventSlug, item.producerUsername)} className="font-display font-semibold text-foreground line-clamp-2 hover:text-primary transition-colors block text-base md:text-lg">{item.eventTitle}</Link>
                  <p className="text-sm text-muted-foreground">{item.tierName}</p>
                  <p className="text-sm font-medium text-foreground mt-1">
                    {item.price === 0 ? t("cart.free") : `${formatBRL(item.price)} ${t("cart.each")}`}
                  </p>
                  {unavailableItems.includes(item.tierId) && (
                    <p className="text-xs text-destructive mt-1 font-medium">{t("cart.insufficientAvailability")}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between sm:justify-end gap-3 pt-3 sm:pt-0 border-t sm:border-t-0 border-border">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground sm:hidden tracking-tight">{t("cart.quantityShort")}</span>
                  <select
                    value={item.quantity}
                    onChange={(e) => updateQuantity(item.tierId, Number(e.target.value))}
                    className="bg-secondary text-foreground text-sm font-medium rounded-lg px-3 py-2 border border-border focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                  >
                    {Array.from({ length: item.maxPerOrder || 10 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center gap-4">
                  <p className="text-base font-bold text-foreground min-w-[80px] text-right">
                    {item.price === 0 ? t("cart.free") : formatBRL(item.price * item.quantity)}
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-full transition-all" aria-label={t("cart.removeAriaLabel")}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="rounded-2xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("cart.removeTicketTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t("cart.removeTicketDesc", { tierName: item.tierName }).replace("<strong>", "").replace("</strong>", "")}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">{t("cart.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => removeItem(item.tierId)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl">
                          {t("cart.remove")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="w-full lg:w-80 shrink-0">
          <div className="p-5 rounded-lg border border-border/80 bg-card space-y-4 sticky top-24">
            <h3 className="font-display font-semibold text-foreground">{t("cart.summary")}</h3>
            <div className="space-y-3">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{t("cart.couponCode")}</label>
              <div className="flex gap-2 w-full">
                <Input
                  placeholder={t("cart.couponPlaceholder")}
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="text-sm flex-1"
                />
                <Button
                  variant="outline"
                  onClick={handleValidateCoupon}
                  disabled={validatingCoupon}
                  className="px-4 whitespace-nowrap"
                >
                  {validatingCoupon ? t("cart.validating") : t("cart.apply")}
                </Button>
              </div>
            </div>
            <div className="space-y-3 text-sm border-t border-border/50 pt-4">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("cart.subtotal")}</span>
                <span className="font-medium text-foreground">{formatBRL(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("cart.serviceFee")}</span>
                <span className="font-medium text-foreground">{formatBRL(platformFee)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-success">
                  <span>{t("cart.discount")}</span>
                  <span className="font-medium">-{formatBRL(discount)}</span>
                </div>
              )}
              <div className="border-t border-border/50 pt-3 flex justify-between">
                <span className="font-semibold text-foreground">{t("cart.total")}</span>
                <span className="text-lg font-bold text-primary">{finalTotal === 0 ? t("cart.free") : formatBRL(finalTotal)}</span>
              </div>
            </div>
            {!user && (
              <div className="flex items-start gap-3 text-xs text-muted-foreground bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
                <LogIn className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{t("cart.authNotice")}</span>
              </div>
            )}
            <Button
              size="lg"
              className="w-full h-12 text-base font-semibold rounded-xl"
              onClick={handleCheckout}
              disabled={unavailableItems.length > 0}
            >
              {unavailableItems.length > 0 ? t("cart.itemsUnavailable") : t("cart.checkoutButton")}
            </Button>
            <AuthModal
              open={showAuth}
              onOpenChange={(open) => { setShowAuth(open); if (!open && user) navigate("/checkout"); }}
              redirectTo="/checkout"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
